import {
  DetailRequirement,
  EngagementTaskTrait,
  FieldModel,
  GeneralResourceDetail,
  MpxItemResponse,
  MpxResponseMeta,
  ProgramEngagementModel,
  TaskDetails,
} from '@cibo/core'
import { useRemoveDetail } from '@cibo/landmanager'
import { useDeleteUserDetails } from '@cibo/profile'
import BugReportRounded from '@mui/icons-material/BugReportRounded'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { UseQueryResult } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useProgramEngagementContext } from '../../../../context'
import { useRemoveProgramEngagementDetail } from '../../../../queries'
import { useUpdateEngagementTaskData } from '../../../../queries/useUpdateEngagementTaskData'

export const DebugDetailResetter = ({
  benchmark,
  dataEntryTrait,
  accordion = false,
}: {
  benchmark?: { details: DetailRequirement[] }
  dataEntryTrait?: EngagementTaskTrait
  accordion?: boolean
}) => {
  const removeUserDetails = useDeleteUserDetails()
  const navigate = useNavigate()
  const removeFieldDetail = useRemoveDetail()
  const removeWorkflowDetail = useRemoveProgramEngagementDetail()

  const { engagement, fieldModels, userId, taskId } = useProgramEngagementContext()
  const updateTaskData = useUpdateEngagementTaskData({
    engagementId: engagement.data?.id,
    taskId,
  })

  if (!benchmark && !dataEntryTrait) {
    return null
  }

  const deleteDetail = (detail: any) => {
    switch (detail.dataType) {
      case 'field':
        return Promise.all(
          fieldModels.data?.items.map(field =>
            removeFieldDetail.mutateAsync({ resourceId: field.resourceId, ...detail })
          ) || []
        )

      case 'workflow':
        return removeWorkflowDetail.mutateAsync({ resourceId: engagement.data?.id, ...detail })

      case 'user':
        return removeUserDetails.mutateAsync({ userId: `${userId}`, details: [detail] })
    }
  }

  const deleteWithDataEntryDetail = (trait: any) => {
    const updateValue = {
      traitId: trait.traitId,
      year: benchmark?.details[0].year,
    }

    const updates =
      trait.dataType === 'user'
        ? {
            userDetails: [{ ...updateValue, value: null }],
          }
        : trait.dataType === 'field'
        ? {
            fieldDetails: fieldModels.data?.items.map(field => ({
              resourceId: field.resourceId,
              details: [{ ...updateValue, input: null, result: null }],
            })),
          }
        : {
            wfDetails: [{ ...updateValue, value: null }],
          }

    // @ts-ignore not sure of type here
    return updateTaskData.mutateAsync({ updates })
  }

  const deleteTooltip = (detail: any) => {
    switch (detail.dataType) {
      case 'field':
        return `Remove any extant ${detail.year || ''} ${detail.traitId} from ${
          fieldModels.data?.items.length
        } fields`
      case 'workflow':
        return `Remove any extant ${detail.traitId} detail from workflow`
      case 'user':
        return `Remove any extant ${detail.traitId} from user`
    }
  }

  return accordion ? (
    <Accordion
      elevation={0}
      square
      sx={{
        backgroundColor: '#ff000033',
        borderColor: 'red',
        borderWidth: 1,
        borderStyle: 'dashed',
        padding: 1,
        borderRadius: 2,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <DebugHeader navigate={navigate} engagement={engagement} />
      </AccordionSummary>
      <AccordionDetails>
        <DebugBody
          benchmark={benchmark}
          deleteTooltip={deleteTooltip}
          dataEntryTrait={dataEntryTrait}
          deleteWithDataEntryDetail={deleteWithDataEntryDetail}
          deleteDetail={deleteDetail}
          engagement={engagement}
        />
      </AccordionDetails>
    </Accordion>
  ) : (
    <Box
      sx={{
        backgroundColor: '#ff000033',
        borderColor: 'red',
        borderWidth: 1,
        borderStyle: 'dashed',
        padding: 1,
        borderRadius: 2,
      }}
    >
      <DebugHeader navigate={navigate} engagement={engagement} />

      <DebugBody
        benchmark={benchmark}
        deleteTooltip={deleteTooltip}
        dataEntryTrait={dataEntryTrait}
        deleteWithDataEntryDetail={deleteWithDataEntryDetail}
        deleteDetail={deleteDetail}
        engagement={engagement}
      />
    </Box>
  )
}

const DebugHeader = ({
  navigate,
  engagement,
}: {
  navigate: NavigateFunction
  engagement: UseQueryResult<ProgramEngagementModel, AxiosError<unknown, any>>
}) => (
  <Stack direction="row" alignItems={'center'} spacing={1}>
    <BugReportRounded color="error" />
    <Typography>Reset Details</Typography>
    <Typography variant="caption">
      Note: this may not immediately refresh the UI. To refresh the UI, try{' '}
      <Button size="small" onClick={() => navigate(0)}>
        page refresh
      </Button>{' '}
      or{' '}
      <Button size="small" onClick={() => engagement.refetch()}>
        engagement refresh
      </Button>{' '}
    </Typography>
  </Stack>
)

const DebugBody = ({
  benchmark,
  deleteTooltip,
  dataEntryTrait,
  deleteWithDataEntryDetail,
  deleteDetail,
  engagement,
}: {
  benchmark?: {
    details: DetailRequirement[]
  }
  deleteTooltip: (detail: any) => string | undefined
  dataEntryTrait: any
  deleteWithDataEntryDetail: (trait: any) => Promise<void | (MpxResponseMeta & TaskDetails)>
  deleteDetail: (
    detail: any
  ) =>
    | Promise<FieldModel[]>
    | Promise<MpxItemResponse<GeneralResourceDetail>>
    | Promise<AxiosResponse<unknown, any>>
    | undefined
  engagement: UseQueryResult<ProgramEngagementModel, AxiosError<unknown, any>>
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Data Type</TableCell>
        <TableCell>Trait ID</TableCell>
        <TableCell>Type</TableCell>
        <TableCell>Year</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {benchmark?.details?.map((benchmarkDetail: any, index: number) => {
        const detailIsDataEntry =
          dataEntryTrait && benchmarkDetail.traitId === dataEntryTrait.traitId
        const detail = detailIsDataEntry ? dataEntryTrait : benchmarkDetail
        const deleteFunction = detailIsDataEntry ? deleteWithDataEntryDetail : deleteDetail
        const tooltipText = detailIsDataEntry
          ? deleteTooltip({ ...dataEntryTrait, year: benchmark?.details[0].year })
          : deleteTooltip(detail)
        return (
          <TableRow key={index}>
            <TableCell>{detail.dataType}</TableCell>
            <TableCell>{detail.traitId}</TableCell>
            <TableCell>{detailIsDataEntry ? 'dataEntry detail' : 'standard detail'}</TableCell>
            <TableCell>{detail.year}</TableCell>
            <TableCell>
              <Tooltip title={tooltipText} placement="top">
                <IconButton
                  onClick={() =>
                    deleteFunction(detail)?.then(() => {
                      engagement.refetch()
                    })
                  }
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        )
      })}
    </TableBody>
  </Table>
)
